import { Typography } from "@mui/material";
import { type GridRowSelectionModel } from "@mui/x-data-grid";
import { useMemo, useState } from "react";

import { useBulkDeleteMailCategoriesMutation } from "api/memberPortal/mailList/mailList.api";
import { MailCategory } from "api/memberPortal/mailList/mailList.types";
import DefaultDataGrid from "components/MUI/DataGrid/DefaultDataGrid";
import BulkActionSnackbar from "components/UI/Snackbars/BulkActionSnackbar";

import { buildMailListCategoriesColumns } from "./MailListCategories.columns";

interface MailListCategoriesTableProps {
  categoryData: MailCategory[];
  isLoading: boolean;
  onEdit: (_category: MailCategory) => void;
  selectedRows: GridRowSelectionModel;
  onSelection: (_category: GridRowSelectionModel) => void;
  handleClearSelection: () => void;
}

export default function MailListCategoriesTable({
  categoryData,
  isLoading,
  onEdit,
  selectedRows,
  onSelection,
  handleClearSelection,
}: MailListCategoriesTableProps) {
  const [bulkDeleteMailCategory, { isLoading: bulkDeleteLoading }] =
    useBulkDeleteMailCategoriesMutation();
  const columns = useMemo(
    () => buildMailListCategoriesColumns({ onEdit }),
    [onEdit],
  );

  const [errorText, setErrorText] = useState<string>("");
  const [confirmShow, setConfirmShow] = useState(false);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    onSelection(newSelection);
    setConfirmShow(false);
  };

  const handleDeleteCategories = async () => {
    if (confirmShow) {
      const ids = selectedRows as number[];

      const result = await bulkDeleteMailCategory({ category_ids: ids });
      if ("error" in result) {
        setErrorText("An error occurred while removing categories");
      } else {
        handleClearSelection();
      }
      setConfirmShow(false);
    } else {
      setConfirmShow(true);
    }
  };

  return (
    <>
      {errorText && <Typography color="error">{errorText}</Typography>}
      <DefaultDataGrid
        rows={categoryData}
        loading={isLoading}
        title={"Mail List Categories"}
        columns={columns}
        showToolbar={false}
        disableCellFocus
        checkboxSelection
        disableZebraStripes
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          "& .MuiDataGrid-row:hover .row-action": {
            opacity: 1,
          },
        }}
      />
      <BulkActionSnackbar
        selectedCount={selectedRows?.length}
        onConfirm={handleDeleteCategories}
        onCancel={handleClearSelection}
        isLoading={bulkDeleteLoading}
        confirmShow={confirmShow}
        actionLabel="Delete Categories"
        confirmLabel={`Confirm? Deleting (${selectedRows.length})`}
        color="error"
      />
    </>
  );
}
